import React, { useState } from "react";
import { LuBell, LuLanguages, LuMonitor, LuSearch, LuStar } from './icons';
import {
    useMMECTheme,
    MMECPalette,
} from "../../contexts/MMECThemeContext";
import { useDataSource } from "../../contexts/DataSourceContext";
import { useLanguage } from "../../contexts/LanguageContext";
import DesktopPairModal from "../DesktopPairModal";
import TrackedContractsPanel from "../TrackedContractsPanel";
import { useContractTracking } from "../../contexts/ContractTrackingContext";
import {
    isFuturesModeEnabled,
    setFuturesModeEnabled,
} from "../../utils/unifiedSymbolMapping";
import { OPEN_PALETTE_EVENT } from "./mmecEvents";

const PALETTE_OPTIONS: { key: MMECPalette; label: string; color: string }[] = [
    { key: "hg", label: "HG — dark terminal", color: "#38bdf8" },
    { key: "sheet", label: "15 — burgundy", color: "#64b3d9" },
    { key: "neon", label: "HK — neon", color: "#00f5ff" },
    { key: "cb", label: "CB — light", color: "#2962ff" },
];

const MOD_KEY =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
        ? "⌘"
        : "Ctrl";

const css = `
  .mmec-topbar {
    display: flex;
    align-items: center;
    gap: 10px;
    height: var(--topbar-h, 46px);
    padding: 0 12px;
    background: var(--topbar-bg);
    border-bottom: 1px solid var(--topbar-border);
    flex-shrink: 0;
    position: relative;
    z-index: 10;
    font-family: var(--font-ui);
  }
  .mmec-topbar.is-simple {
    height: auto;
    min-height: 56px;
    padding: max(8px, env(safe-area-inset-top, 0px)) max(10px, env(safe-area-inset-right, 0px)) 8px max(10px, env(safe-area-inset-left, 0px));
    gap: 8px;
    flex-wrap: wrap;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  /* ── Brand ─────────────────────────────────────────────────────────── */
  .mmec-brand {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    flex-shrink: 0;
    color: var(--txt);
    border-radius: 6px;
  }
  .mmec-brand-mark {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: var(--acc);
    color: var(--void);
    display: grid;
    place-items: center;
    font: 700 12px/1 var(--font-mono);
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--acc) 35%, transparent);
  }
  .mmec-brand-word {
    font: 600 12.5px/1 var(--font-ui);
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  /* ── Search trigger (centre) ───────────────────────────────────────── */
  .mmec-topbar-search {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 30px;
    width: min(320px, 100%);
    padding: 0 8px 0 10px;
    margin: 0 auto;
    border: 1px solid var(--hair);
    border-radius: 8px;
    background: var(--base);
    color: var(--muted);
    font: 400 12px/1 var(--font-ui);
    cursor: pointer;
    transition: border-color var(--dur-micro) var(--ease), color var(--dur-micro) var(--ease), background var(--dur-micro) var(--ease);
  }
  .mmec-topbar-search:hover {
    border-color: var(--border);
    color: var(--txt-2);
    background: var(--elev);
  }
  .mmec-topbar-search span:first-of-type { flex: 1; text-align: start; }
  .mmec-topbar-centre {
    flex: 1;
    min-width: 0;
    display: flex;
    justify-content: center;
    padding: 0 12px;
  }
  @media (max-width: 1100px) {
    .mmec-topbar-search { width: auto; }
    .mmec-topbar-search span:first-of-type { display: none; }
  }

  /* ── Right cluster ─────────────────────────────────────────────────── */
  .mmec-topbar-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }
  .mmec-topbar-select {
    height: 28px;
    padding: 0 26px 0 9px;
    border: 1px solid var(--hair);
    border-radius: 8px;
    background: transparent;
    color: var(--muted);
    font: 500 11px/1 var(--font-ui);
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    background-image: linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%);
    background-position: calc(100% - 13px) 50%, calc(100% - 9px) 50%;
    background-size: 4px 4px;
    background-repeat: no-repeat;
    transition: color var(--dur-micro) var(--ease), border-color var(--dur-micro) var(--ease);
  }
  .mmec-topbar-select:hover { color: var(--txt); border-color: var(--border); }
  .mmec-topbar-select option { background: var(--elev); color: var(--txt); }
  .mmec-topbar-select.is-touch { height: 40px; font-size: 13px; border-radius: 10px; }

  /* ── Palette dots ──────────────────────────────────────────────────── */
  .mmec-palette-dots {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid var(--hair);
    border-radius: 999px;
    flex-shrink: 0;
  }
  .mmec-palette-dot {
    width: 14px;
    height: 14px;
    padding: 0;
    border-radius: 50%;
    border: 2px solid transparent;
    background-clip: content-box;
    cursor: pointer;
    transition: transform var(--dur-control) var(--ease), border-color var(--dur-control) var(--ease);
  }
  .mmec-palette-dot:hover { transform: scale(1.15); }
  .mmec-palette-dot.is-active {
    border-color: var(--txt);
    transform: scale(1.1);
  }
  .mmec-palette-dots.is-touch { height: 40px; gap: 10px; padding: 0 12px; }
  .mmec-palette-dots.is-touch .mmec-palette-dot { width: 20px; height: 20px; }

  .mmec-topbar-spacer { flex: 1; }

  /* Simple shell on a phone: the control cluster takes its own row and pans
     sideways rather than clipping at the edge. The scrollbar is hidden — the
     row's cut-off last control is the affordance, as on any mobile tab strip. */
  .mmec-topbar.is-simple .mmec-topbar-controls {
    flex: 1 0 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 2px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .mmec-topbar.is-simple .mmec-topbar-controls::-webkit-scrollbar { display: none; }
  .mmec-topbar.is-simple .mmec-topbar-controls > * { flex-shrink: 0; }
`;

export interface MMECTopBarProps {
    /** Simple shell: compact bar; shows a Pro control to return to advanced mode. */
    simpleMode?: boolean;
    /** Simple shell: open flow / liquidity alert settings */
    onOpenFlowAlerts?: () => void;
    /** Design System shell only: the active tool's title, shown first in the bar (artboard web/_topbar). */
    dsTitle?: string;
}

export const MMECTopBar: React.FC<MMECTopBarProps> = ({
    simpleMode = false,
    onOpenFlowAlerts,
    dsTitle,
}) => {
    const { palette, setPalette, mode, setMode } = useMMECTheme();
    const { dataSource, setDataSource } = useDataSource();
    const { language, toggleLanguage } = useLanguage();
    const { trackedContracts } = useContractTracking();
    const [pairModalOpen, setPairModalOpen] = useState(false);
    const [trackingPanelOpen, setTrackingPanelOpen] = useState(false);
    const [futuresEnabled, setFuturesEnabled] = useState(() => isFuturesModeEnabled());

    const isAr = language === "ar";
    const touch = simpleMode ? " is-touch" : "";

    const handleFuturesToggle = () => {
        const next = !futuresEnabled;
        setFuturesEnabled(next);
        setFuturesModeEnabled(next);
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("mmecFuturesModeChanged", { detail: { enabled: next } }),
            );
        }
    };

    const openPalette = () => {
        window.dispatchEvent(new Event(OPEN_PALETTE_EVENT));
    };

    const trackedCount = trackedContracts.length;

    return (
        <>
            <style>{css}</style>
            <header
                className={`mmec-topbar${simpleMode ? " is-simple" : ""}`}
                data-tour="mmec-top-bar"
            >
                {/* Brand */}
                <a href="/" className="mmec-brand" aria-label="MMEC home">
                    <div className="mmec-brand-mark" aria-hidden>M</div>
                    <span className="mmec-brand-word">MMEC</span>
                </a>

                <div className="mmec-vsep" aria-hidden />
                {dsTitle !== undefined && <h1 className="mmec-ds-title">{dsTitle}</h1>}

                {/* Experience mode */}
                {simpleMode ? (
                    <>
                        {onOpenFlowAlerts && (
                            <button
                                type="button"
                                className="mmec-btn is-touch"
                                onClick={onOpenFlowAlerts}
                                title={isAr ? "تنبيهات التدفق والسيولة" : "Flow & liquidity alerts"}
                                aria-label={isAr ? "تنبيهات التدفق" : "Flow alerts"}
                            >
                                <LuBell strokeWidth={1.75} aria-hidden />
                            </button>
                        )}
                        <button
                            type="button"
                            className="mmec-btn is-touch"
                            onClick={() => setMode("advanced")}
                            title={isAr ? "الوضع المتقدم — كل الأدوات" : "Advanced mode — all tools"}
                        >
                            {isAr ? "احترافي" : "Pro"}
                        </button>
                    </>
                ) : (
                    <div
                        className="mmec-seg"
                        role="group"
                        aria-label="Experience mode"
                        style={{ "--n": 2, "--i": mode === "simple" ? 0 : 1 } as React.CSSProperties}
                    >
                        <button
                            type="button"
                            className={`mmec-seg-btn${mode === "simple" ? " is-active" : ""}`}
                            onClick={() => setMode("simple")}
                            aria-pressed={mode === "simple"}
                            title="Simple mode — 5 core tools"
                        >
                            Simple
                        </button>
                        <button
                            type="button"
                            className={`mmec-seg-btn${mode === "advanced" ? " is-active" : ""}`}
                            onClick={() => setMode("advanced")}
                            aria-pressed={mode === "advanced"}
                            title="Advanced mode — all tools"
                        >
                            Pro
                        </button>
                    </div>
                )}

                {/* Search — Pro only; the simple shell navigates by tab bar */}
                {simpleMode ? (
                    <div className="mmec-topbar-spacer" />
                ) : (
                    <div className="mmec-topbar-centre">
                        <button
                            type="button"
                            className="mmec-topbar-search"
                            onClick={openPalette}
                            title={`${isAr ? "ابحث عن أداة" : "Search tools"} (${MOD_KEY} K)`}
                        >
                            <LuSearch size={14} strokeWidth={2} aria-hidden />
                            <span>{isAr ? "انتقل إلى أي أداة…" : "Jump to any tool…"}</span>
                            <span className="mmec-kbd" aria-hidden>{MOD_KEY} K</span>
                        </button>
                    </div>
                )}

                {/* Controls */}
                <div className="mmec-topbar-controls">
                    <select
                        className={`mmec-topbar-select${touch}`}
                        value={dataSource}
                        data-tour="mmec-data-source"
                        onChange={(e) => setDataSource(e.target.value as "rtl_legacy" | "stable")}
                        title="Data source"
                        aria-label={isAr ? "مصدر البيانات" : "Data source"}
                    >
                        <option value="rtl_legacy">Legacy RTL</option>
                        <option value="stable">Stable DB</option>
                    </select>

                    <button
                        type="button"
                        className={`mmec-switch${futuresEnabled ? " is-on" : ""}${touch}`}
                        onClick={handleFuturesToggle}
                        role="switch"
                        aria-checked={futuresEnabled}
                        title={
                            futuresEnabled
                                ? "Futures mapping on (ES→SPY, NQ→QQQ…) — click to turn off"
                                : "Map futures to their ETF options (ES→SPY, NQ→QQQ…)"
                        }
                    >
                        <span className="mmec-switch-track" aria-hidden />
                        <span>{isAr ? "عقود آجلة" : "Futures"}</span>
                    </button>

                    <button
                        type="button"
                        className={`mmec-btn${trackedCount > 0 ? " is-warn" : ""}${touch}`}
                        onClick={() => setTrackingPanelOpen((o) => !o)}
                        title={isAr ? "العقود المتتبعة" : "Tracked contracts"}
                        aria-label={`${isAr ? "العقود المتتبعة" : "Tracked contracts"}${trackedCount ? ` (${trackedCount})` : ""}`}
                        aria-expanded={trackingPanelOpen}
                    >
                        <LuStar
                            strokeWidth={1.75}
                            fill={trackedCount > 0 ? "currentColor" : "none"}
                            aria-hidden
                        />
                        {trackedCount > 0 && (
                            <span className="mmec-badge"><span>{trackedCount}</span></span>
                        )}
                    </button>

                    <button
                        type="button"
                        className={`mmec-btn${pairModalOpen ? " is-active" : ""}${touch}`}
                        onClick={() => setPairModalOpen(true)}
                        title={isAr ? "ربط تطبيق سطح المكتب" : "Pair desktop app"}
                        aria-label={isAr ? "ربط تطبيق سطح المكتب" : "Pair desktop app"}
                    >
                        <LuMonitor strokeWidth={1.75} aria-hidden />
                    </button>

                    <button
                        type="button"
                        className={`mmec-btn${touch}`}
                        onClick={toggleLanguage}
                        title={language === "en" ? "التبديل إلى العربية" : "Switch to English"}
                        aria-label={language === "en" ? "Switch to Arabic" : "Switch to English"}
                    >
                        <LuLanguages strokeWidth={1.75} aria-hidden />
                        <span>{language === "en" ? "AR" : "EN"}</span>
                    </button>

                    <div
                        className={`mmec-palette-dots${touch}`}
                        role="radiogroup"
                        aria-label={isAr ? "لوحة الألوان" : "Color palette"}
                    >
                        {PALETTE_OPTIONS.map(({ key, label, color }) => (
                            <button
                                key={key}
                                type="button"
                                role="radio"
                                aria-checked={palette === key}
                                className={`mmec-palette-dot${palette === key ? " is-active" : ""}`}
                                style={{ backgroundColor: color }}
                                onClick={() => setPalette(key)}
                                title={label}
                                aria-label={label}
                            />
                        ))}
                    </div>
                </div>

                <DesktopPairModal
                    isOpen={pairModalOpen}
                    onClose={() => setPairModalOpen(false)}
                />
                <TrackedContractsPanel
                    isOpen={trackingPanelOpen}
                    onClose={() => setTrackingPanelOpen(false)}
                    colors={{
                        surface: "var(--elev)",
                        surfaceBorder: "var(--hair)",
                        background: "var(--base)",
                        text: "var(--txt)",
                        textMuted: "var(--muted)",
                        textSecondary: "var(--muted)",
                        primary: "var(--acc)",
                        success: "var(--up)",
                        error: "var(--down)",
                    }}
                />
            </header>
        </>
    );
};

export default MMECTopBar;
